import { ExpandIcon, ShrinkIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useBodyClass } from '~/hooks/use-body-class';
import { sheeter } from '~/modules/common/sheeter/use-sheeter';
import { toaster } from '~/modules/common/toaster/toaster';
import { TooltipButton } from '~/modules/common/tooltip-button';
import { useNavigationStore } from '~/modules/navigation/navigation-store';
import { Button } from '~/modules/ui/button';
import { useUIStore } from '~/modules/ui/ui-store';
import { cn } from '~/utils/cn';

interface FocusViewProps {
  className?: string;
  iconOnly?: boolean;
}

interface FocusViewContainerProps {
  children: ReactNode;
  className?: string;
  disabled?: boolean;
}

/** Toggles focus view mode, which hides non-essential UI. */
export function FocusView({ className = '', iconOnly }: FocusViewProps) {
  const { t } = useTranslation();
  const focusView = useUIStore((state) => state.focusView);
  const setFocusView = useUIStore((state) => state.setFocusView);
  const setNavSheetOpen = useNavigationStore((state) => state.setNavSheetOpen);

  const toggleFocusView = () => {
    toaster.success(focusView ? t('c:left_focus') : t('c:entered_focus'));
    setFocusView(!focusView);
    sheeter.getState().remove();
    setNavSheetOpen(null);
    window.scrollTo(0, 0);
  };

  return (
    <TooltipButton toolTipContent={t('c:focus_view')} disabled={!iconOnly} className="max-lg:hidden">
      <Button variant="outline" className={cn('flex', className)} onClick={toggleFocusView}>
        {focusView ? <ShrinkIcon /> : <ExpandIcon />}
        {!iconOnly && <span>{focusView ? t('c:leave_focus_view') : t('c:focus_view')}</span>}
      </Button>
    </TooltipButton>
  );
}

/**
 * Wraps the page's main content. While focus view is active, everything else in the enclosing `.focus-view-scope`
 * is hidden; chrome outside a scope opts in with the `focus-view:hidden` variant. It is the page `container`, so
 * content rendered inside never adds its own: a second one doubles the side gutter.
 */
export function FocusViewContainer({ children, className = '', disabled }: FocusViewContainerProps) {
  const focusView = useUIStore((state) => state.focusView);

  const isActive = focusView && !disabled;

  useBodyClass({ 'focus-view': isActive });

  return (
    <div
      className={cn(
        'focus-view-container container flex min-h-svh flex-col gap-2 pt-3',
        className,
        isActive && 'min-h-full w-full max-w-none min-w-full',
      )}
    >
      {children}
    </div>
  );
}
